import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import emailService from '../services/emailService';
import SentimentChart from '../components/charts/SentimentChart';
import CommunicationChart from '../components/charts/CommunicationChart';
import AnalysisTypeChart from '../components/charts/AnalysisTypeChart';
import PieChart from '../components/charts/PieChart';
import EmailAnalysisModal from '../components/EmailAnalysisModal';

function Analytics() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aggregatedData, setAggregatedData] = useState(null);
  const [selectedEmailId, setSelectedEmailId] = useState(null);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    // Ne faire l'appel API que si l'utilisateur est authentifié
    if (isAuthenticated) {
      loadStats();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Charger les statistiques de base
      const statsData = await emailService.getStats();
      setStats(statsData);

      // Charger les données agrégées pour les graphiques
      try {
        const sentimentData = await emailService.getAggregatedData('sentiment_trends');
        const communicationData = await emailService.getAggregatedData('communication_patterns');
        
        setAggregatedData({
          sentiment_trends: sentimentData,
          communication_patterns: communicationData
        });
      } catch (aggError) {
        console.warn('Erreur lors du chargement des données agrégées:', aggError);
        // Utiliser des données de fallback si l'agrégation échoue
        setAggregatedData({
          sentiment_trends: getFallbackSentimentData(statsData),
          communication_patterns: getFallbackCommunicationData(statsData)
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getFallbackSentimentData = (stats) => ({
    distribution: [
      { category: 'positive', count: 0, percentage: 0 },
      { category: 'neutral', count: 0, percentage: 0 },
      { category: 'negative', count: 0, percentage: 0 }
    ],
    overall_sentiment: {
      average: stats?.averageSentiment || 0,
      total_analyses: stats?.analyzedEmails || 0
    }
  });

  const getFallbackCommunicationData = (stats) => ({
    total_emails: stats?.totalEmails || 0,
    time_period: { start: null, end: null },
    top_senders: [],
    frequency_analysis: { daily_average: 0, peak_days: [] },
    thread_analysis: []
  });

  const getPercentage = (value, total) => {
    if (!total || total === 0) return 0;
    // Correction: éviter les pourcentages > 100% dus aux multiples analyses par email
    const percentage = (value / total) * 100;
    return Math.min(percentage, 100).toFixed(1);
  };

  const getAnalysisPercentage = (analyzedEmails, totalEmails) => {
    if (!totalEmails || totalEmails === 0) return 0;
    // Pourcentage basé sur le nombre d'emails analysés, pas le nombre d'analyses
    return ((analyzedEmails / totalEmails) * 100).toFixed(1);
  };

  const handleChartClick = (emailId) => {
    if (emailId) {
      setSelectedEmailId(emailId);
      setShowAnalysisModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowAnalysisModal(false);
    setSelectedEmailId(null);
  };

  if (loading) {
    return (
      <div className="container-fluid py-6">
        <div className="text-center py-12">
          <div className="spinner-border text-blue-600 mb-4" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="text-slate-600">Chargement des analyses...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid py-6">
        <div className="alert alert-danger mb-6" role="alert">
          <i className="bi bi-exclamation-triangle mr-2"></i>
          {error}
        </div>
        <button className="btn btn-primary" onClick={loadStats}>
          <i className="bi bi-arrow-clockwise mr-2"></i>
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="container-fluid py-6">
      {/* Modal d'analyse d'email */}
      {showAnalysisModal && (
        <EmailAnalysisModal
          emailId={selectedEmailId}
          onClose={handleCloseModal}
        />
      )}
      {/* Header */}
      <div className="page-header">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="page-title">
              <i className="bi bi-graph-up mr-3"></i>
              Analyses et Statistiques
            </h1>
            <p className="page-subtitle">
              Vue d'ensemble de vos données d'emails
            </p>
          </div>
          <div className="flex space-x-3">
            <button className="btn btn-outline-primary" onClick={loadStats}>
              <i className="bi bi-arrow-clockwise mr-2"></i>
              Actualiser
            </button>
            <button
              className="btn btn-primary"
              onClick={() => window.location.href = '/insights'}
            >
              <i className="bi bi-lightbulb mr-2"></i>
              Analyses avancées
            </button>
          </div>
        </div>
      </div>

      {stats ? (
        <>
          {/* Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="metric-card">
              <div className="metric-value text-blue-600">
                {stats.totalEmails || 0}
              </div>
              <div className="metric-label">Emails importés</div>
            </div>

            <div className="metric-card">
              <div className="metric-value text-green-600">
                {stats.analyzedEmails || 0}
              </div>
              <div className="metric-label">Analyses</div>
            </div>

            <div className="metric-card">
              <div className="metric-value text-purple-600">
                {stats.driveAvailable ? 'Actif' : 'Inactif'}
              </div>
              <div className="metric-label">Google Drive</div>
            </div>

            <div className="metric-card">
              <div className="metric-value text-amber-600">
                {getAnalysisPercentage(stats.analyzedEmails, stats.totalEmails)}%
              </div>
              <div className="metric-label">Taux d'analyse</div>
            </div>
          </div>

          {/* Charts Section - Version améliorée avec plus de graphiques */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Types d'analyse */}
            <div className="analysis-card">
              <div className="card-header">
                <h5 className="text-lg font-semibold mb-0">
                  <i className="bi bi-pie-chart mr-2"></i>
                  Types d'analyse
                </h5>
              </div>
              <div className="card-body">
                <AnalysisTypeChart
                  totalEmails={stats.totalEmails}
                  data={[
                    { label: 'Analyse de sentiment', value: stats.analyzedEmails || 0 },
                    { label: 'Classification thématique', value: stats.analyzedEmails || 0 },
                    { label: 'Extraction entités', value: stats.analyzedEmails || 0 },
                    { label: 'Analyse syntaxique', value: stats.analyzedEmails || 0 }
                  ]}
                  onChartClick={handleChartClick}
                />
              </div>
            </div>

            {/* Sentiment Chart */}
            <div className="analysis-card">
              <div className="card-header">
                <h5 className="text-lg font-semibold mb-0">
                  <i className="bi bi-heart mr-2"></i>
                  Analyse des sentiments
                </h5>
              </div>
              <div className="card-body">
                <SentimentChart
                  data={aggregatedData?.sentiment_trends}
                  onChartClick={handleChartClick}
                />
              </div>
            </div>

            {/* Communication Patterns Chart */}
            <div className="analysis-card">
              <div className="card-header">
                <h5 className="text-lg font-semibold mb-0">
                  <i className="bi bi-graph-up mr-2"></i>
                  Patterns de communication
                </h5>
              </div>
              <div className="card-body">
                <CommunicationChart
                  data={aggregatedData?.communication_patterns}
                  onChartClick={handleChartClick}
                />
              </div>
            </div>

            {/* Distribution des emails par statut */}
            <div className="analysis-card">
              <div className="card-header">
                <h5 className="text-lg font-semibold mb-0">
                  <i className="bi bi-bar-chart mr-2"></i>
                  Statut des emails
                </h5>
              </div>
              <div className="card-body">
                <PieChart
                  data={[
                    { label: 'Analysés', value: stats.analyzedEmails || 0 },
                    { label: 'Non analysés', value: Math.max(0, (stats.totalEmails || 0) - (stats.analyzedEmails || 0)) }
                  ]}
                  title="Distribution analyse/non-analyse"
                  colors={['#10b981', '#6b7280']}
                  size={160}
                />
              </div>
            </div>
          </div>

          {/* System Status */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="analysis-card">
              <div className="card-header">
                <h5 className="text-lg font-semibold mb-0">
                  <i className="bi bi-gear mr-2"></i>
                  État du système
                </h5>
              </div>
              <div className="card-body">
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="font-medium text-slate-800">Service Email</span>
                    <span className="badge bg-green-100 text-green-800">Actif</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="font-medium text-slate-800">Google Drive</span>
                    <span className={`badge ${stats.driveAvailable ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {stats.driveAvailable ? 'Actif' : 'Inactif'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="font-medium text-slate-800">Dernière mise à jour</span>
                    <span className="text-sm text-slate-500">
                      {stats.lastUpdated ? new Date(stats.lastUpdated).toLocaleString('fr-FR') : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Performance Metrics */}
            <div className="analysis-card">
              <div className="card-header">
                <h5 className="text-lg font-semibold mb-0">
                  <i className="bi bi-speedometer2 mr-2"></i>
                  Métriques de performance
                </h5>
              </div>
              <div className="card-body">
                <div className="space-y-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-2">
                      {getAnalysisPercentage(stats.analyzedEmails, stats.totalEmails)}%
                    </div>
                    <div className="text-sm text-slate-500">Taux de traitement</div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="text-center">
                      <div className="text-lg font-semibold text-slate-800">{stats.totalEmails || 0}</div>
                      <div className="text-slate-500">Total importé</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-semibold text-slate-800">{stats.analyzedEmails || 0}</div>
                      <div className="text-slate-500">Analyses complétées</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="analysis-card">
            <div className="card-header">
              <h5 className="text-lg font-semibold mb-0">
                <i className="bi bi-clipboard-data mr-2"></i>
                Résumé des opérations
              </h5>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center">
                  <i className="bi bi-envelope-check text-3xl text-blue-600 mb-2 block"></i>
                  <div className="text-2xl font-bold text-slate-900">{stats.totalEmails || 0}</div>
                  <div className="text-sm text-slate-500">Emails importés</div>
                </div>
                <div className="text-center">
                  <i className="bi bi-speedometer2 text-3xl text-green-600 mb-2 block"></i>
                  <div className="text-2xl font-bold text-slate-900">{getAnalysisPercentage(stats.analyzedEmails, stats.totalEmails)}%</div>
                  <div className="text-sm text-slate-500">Taux d'analyse</div>
                </div>
                <div className="text-center">
                  <i className="bi bi-heart text-3xl text-purple-600 mb-2 block"></i>
                  <div className="text-2xl font-bold text-slate-900">{((stats.averageSentiment || 0) * 100).toFixed(1)}%</div>
                  <div className="text-sm text-slate-500">Sentiment moyen</div>
                </div>
                <div className="text-center">
                  <i className="bi bi-cloud text-3xl text-amber-600 mb-2 block"></i>
                  <div className="text-2xl font-bold text-slate-900">
                    {stats.driveAvailable ? 'Actif' : 'Inactif'}
                  </div>
                  <div className="text-sm text-slate-500">Stockage Drive</div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-12">
          <i className="bi bi-graph-down text-6xl text-slate-300 mb-4 block"></i>
          <h3 className="text-xl font-semibold text-slate-700 mb-2">Aucune donnée disponible</h3>
          <p className="text-slate-500 mb-6">Importez des emails pour voir les analyses</p>
          <button 
            onClick={() => window.location.href = '/'}
            className="btn btn-primary"
          >
            <i className="bi bi-upload mr-2"></i>
            Importer des emails
          </button>
        </div>
      )}
    </div>
  );
}

export default Analytics;